import { useEffect, useRef, useState } from "react";
import axios from "../axios";


// ─── Minimal icon helpers (inline SVG) ───────────────────────────────────────
const Icon = ({ d, size = 16, stroke = "currentColor", fill = "none", strokeWidth = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const SearchIcon = () => <Icon size={15} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />;
const SendIcon = () => <Icon size={16} d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />;
const PaperclipIcon = () => <Icon size={16} d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />;
const BellIcon = () => <Icon size={16} d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />;
const VolumeIcon = () => <Icon size={16} d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />;
const BotIcon = () => <Icon size={14} d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7H3a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2zM5 14v6a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-6M9 11h.01M15 11h.01" />;
const TagIcon = () => <Icon size={13} d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7h.01" />;
const ClockIcon = () => <Icon size={12} d="M12 22c5.52 0 10-4.48 10-10S17.52 2 12 2 2 6.48 2 12s4.48 10 10 10zM12 6v6l4 2" />;
const ChevronLeft = () => <Icon size={20} d="M15 18l-6-6 6-6" />;
const MoreIcon = () => <Icon size={20} d="M12 5v.01M12 12v.01M12 19v.01" strokeWidth={2.5} />;

// ─── Status badge colors ──────────────────────────────────────────────────────
const statusColors = {
  New: { bg: "#1a3a5c", text: "#60a5fa" },
  Interested: { bg: "#14362a", text: "#34d399" },
  "Not Interested": { bg: "#3b1a1a", text: "#f87171" },
  Booked: { bg: "#2a1f4a", text: "#a78bfa" },
  "Follow-up": { bg: "#2a2a0f", text: "#fbbf24" },
  Closed: { bg: "#1a1a2a", text: "#94a3b8" },
};

const StatusBadge = ({ status }) => {
  const colors = statusColors[status] || statusColors["New"];
  return (
    <span style={{
      background: colors.bg, color: colors.text,
      padding: "2px 8px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, letterSpacing: "0.03em",
      fontFamily: "inherit",
    }}>
      {status || "New"}
    </span>
  );
};

// ─── Avatar ───────────────────────────────────────────────────────────────────
const avatarColors = ["#6366f1","#8b5cf6","#ec4899","#14b8a6","#f59e0b","#3b82f6","#10b981"];
const Avatar = ({ name, size = 38 }) => {
  const letter = (name || "?")[0].toUpperCase();
  const color = avatarColors[letter.charCodeAt(0) % avatarColors.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `linear-gradient(135deg, ${color}cc, ${color}55)`,
      border: `1.5px solid ${color}66`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.38, fontWeight: 700, color: "#fff",
      flexShrink: 0, userSelect: "none",
      boxShadow: `0 2px 10px ${color}44`,
    }}>
      {letter}
    </div>
  );
};

const formatDate = (ts) => {
  if (!ts) return "";
  return new Date(ts).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

const formatShortTime = (ts) => {
  if (!ts) return "";

  const d = new Date(ts);
  const today = new Date();

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const time = d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  if (d.toDateString() === today.toDateString()) {
    return `Today ${time}`;
  }

  if (d.toDateString() === yesterday.toDateString()) {
    return `Yesterday ${time}`;
  }

  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }) + ` ${time}`;
};

// ─── Mobile bottom sheet / drawer for meta info ───────────────────────────────
const MobileMetaDrawer = ({ open, onClose, children }) => (
  <>
    {open && (
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.6)",
          zIndex: 40,
        }}
      />
    )}
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0,
      background: "#0d1117",
      borderTop: "1px solid #1e2736",
      borderRadius: "16px 16px 0 0",
      padding: "16px 16px 32px",
      zIndex: 50,
      transform: open ? "translateY(0)" : "translateY(110%)",
      transition: "transform 0.3s cubic-bezier(0.32,0.72,0,1)",
      maxHeight: "80vh",
      overflowY: "auto",
    }}>
      <div style={{
        width: 36, height: 4, background: "#1e2736",
        borderRadius: 2, margin: "0 auto 16px",
      }} />
      {children}
    </div>
  </>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Inbox() {
  const [chats, setChats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingMedia, setSendingMedia] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [mediaCaption, setMediaCaption] = useState("");
  const [botPaused, setBotPaused] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [leadStatus, setLeadStatus] = useState("New");
  const messagesEndRef = useRef(null);
  const messagesBoxRef = useRef(null);
  const previousChatUnread = useRef({});
  const [followupAt, setFollowupAt] = useState("");
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMetaDrawer, setShowMetaDrawer] = useState(false);
  const [showMediaBar, setShowMediaBar] = useState(false);
  const fileInputRef = useRef(null);

  const playBeep = () => {
    if (!soundEnabled) return;
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContext();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = 880;
      gainNode.gain.value = 0.2;
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.15);
    } catch (err) { console.log("Sound blocked:", err.message); }
  };

  const showDesktopNotification = (chat) => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      new Notification("New WhatsApp Message", {
        body: `${chat.profile_name || chat.phone}: ${chat.last_message || ""}`,
        icon: "/logo192.png"
      });
    }
  };

  const loadChats = async () => {
    try {
      const res = await axios.get("/inbox/chats");
      const newChats = res.data || [];
      newChats.forEach((chat) => {
        const oldUnread = Number(previousChatUnread.current[chat.phone] || 0);
        const newUnread = Number(chat.unread_count || 0);
        if (newUnread > oldUnread) { playBeep(); showDesktopNotification(chat); }
        previousChatUnread.current[chat.phone] = newUnread;
      });
      setChats(newChats);
    } catch (err) { console.error("Load chats error:", err.response?.data || err.message); }
  };

  const loadMessages = async (phone, markRead = true) => {
    try {
      const isNewChat = phone !== selected;
      if (isNewChat) setShouldAutoScroll(true);
      setSelected(phone);
      const res = await axios.get(`/inbox/chat/${phone}`);
      setMessages(res.data || []);
      const sessionRes = await axios.get(`/inbox/session/${phone}`);
      setBotPaused(Number(sessionRes.data.human_handover || 0) === 1);
      setLeadStatus(sessionRes.data.lead_status || "New");
      setFollowupAt(sessionRes.data.followup_at ? sessionRes.data.followup_at.slice(0, 16) : "");
      setNotes(sessionRes.data.notes || "");
      setTags(sessionRes.data.tags || "");
      if (markRead) {
        await axios.post("/inbox/mark-read", { phone });
        previousChatUnread.current[phone] = 0;
        loadChats();
      }
    } catch (err) { console.error("Load messages error:", err.response?.data || err.message); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected || sending) return;
    setSending(true);
    try {
      await axios.post("/inbox/send", { phone: selected, message: reply });
      setReply("");
      setShouldAutoScroll(true);
      loadMessages(selected);
      loadChats();
    } catch (err) { console.error("Send reply error:", err.response?.data || err.message); alert("Message send failed ❌"); }
    finally { setSending(false); }
  };

  const sendMedia = async () => {
    if (!selected || !selectedFile || sendingMedia) return;
    setSendingMedia(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const uploadRes = await axios.post("/inbox/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const file = uploadRes.data.file;
      await axios.post("/inbox/send-media", { phone: selected, fileUrl: file.url, fileType: file.mimetype, caption: mediaCaption });
      setSelectedFile(null);
      setMediaCaption("");
      setShouldAutoScroll(true);
      loadMessages(selected);
      loadChats();
      alert("Media sent ✅");
    } catch (err) { console.error("Media send error:", err.response?.data || err.message); alert("Media send failed ❌"); }
    finally { setSendingMedia(false); }
  };

  const toggleBotForChat = async () => {
    if (!selected) return;
    try {
      await axios.post("/inbox/bot-toggle", { phone: selected, pause: !botPaused });
      setBotPaused(!botPaused);
      loadChats();
    } catch (err) { console.error("Bot toggle error:", err.response?.data || err.message); }
  };

  const updateLeadStatus = async (status) => {
    if (!selected) return;
    try {
      await axios.post("/inbox/lead-status", { phone: selected, status });
      setLeadStatus(status);
      loadChats();
    } catch (err) { console.error("Lead status error:", err.response?.data || err.message); }
  };

  const saveFollowup = async () => {
    if (!selected || !followupAt) return;
    try {
      await axios.post("/inbox/followup", { phone: selected, followup_at: followupAt });
      setLeadStatus("Follow-up");
      loadChats();
      alert("Follow-up saved ✅");
    } catch (err) { console.error("Follow-up error:", err.response?.data || err.message); alert("Follow-up save failed ❌"); }
  };

  const saveNotesTags = async () => {
    if (!selected) return;
    try {
      await axios.post("/inbox/notes-tags", { phone: selected, notes, tags });
      loadChats();
      alert("Notes & Tags saved ✅");
    } catch (err) { console.error("Notes tags error:", err.response?.data || err.message); alert("Notes & Tags save failed ❌"); }
  };

  const handleMessagesScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 120;
    setShouldAutoScroll(isNearBottom);
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    loadChats();
    const timer = setInterval(() => {
      loadChats();
      if (selected) loadMessages(selected, false);
    }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [selected, soundEnabled]);

  useEffect(() => {
    if (shouldAutoScroll) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, shouldAutoScroll]);

  const filteredChats = chats.filter((chat) => {
    const keyword = search.toLowerCase();
    const matchSearch =
      String(chat.phone || "").toLowerCase().includes(keyword) ||
      String(chat.profile_name || "").toLowerCase().includes(keyword) ||
      String(chat.last_message || "").toLowerCase().includes(keyword) ||
      String(chat.lead_status || "").toLowerCase().includes(keyword);
    const unread = Number(chat.unread_count || 0);
    if (filter === "unread") return matchSearch && unread > 0;
    if (filter === "read") return matchSearch && unread === 0;
    if (filter === "followup") return matchSearch && String(chat.lead_status || "") === "Follow-up";
    if (filter === "interested") return matchSearch && String(chat.lead_status || "") === "Interested";
    if (filter === "booked") return matchSearch && String(chat.lead_status || "") === "Booked";
    if (filter === "new") return matchSearch && String(chat.lead_status || "New") === "New";
    const today = new Date().toISOString().slice(0, 10);
    const followupDate = chat.followup_at ? String(chat.followup_at).slice(0, 10) : "";
    if (filter === "due_today") return matchSearch && followupDate === today;
    if (filter === "overdue") return matchSearch && chat.followup_at && new Date(chat.followup_at) < new Date();
    return matchSearch;
  });

  const selectedChat = chats.find(c => c.phone === selected);

  const downloadContactsCSV = () => {
  const data = filteredChats.length > 0 ? filteredChats : chats;

  if (!data.length) {
    alert("No contacts found");
    return;
  }

  const headers = [
    "Name",
    "Phone",
    "Category",
    "Last Message",
    "Last Chat Time",
    "Follow-up Time",
    "Tags",
    "Notes"
  ];

  const rows = data.map((chat) => [
    chat.profile_name || "",
    chat.phone || "",
    chat.lead_status || "New",
    chat.last_message || "",
    formatShortTime(chat.last_time || chat.last_message_time || chat.created_at),
    chat.followup_at ? formatDate(chat.followup_at) : "",
    chat.tags || "",
    chat.notes || ""
  ]);

  const csvContent = [
    headers,
    ...rows
  ]
    .map(row =>
      row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(",")
    )
    .join("\n");

  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;"
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `crm_contacts_${filter}_${new Date().toISOString().slice(0, 10)}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

  // ── Responsive show/hide logic ────────────────────────────────────────────
  // On mobile: show sidebar OR chat panel (not both)
  // On desktop: show both side by side
  const showSidebar = !isMobile || !selected;
  const showMainPanel = !isMobile || !!selected;

  // ── Styles ────────────────────────────────────────────────────────────────
  const S = {
    root: {
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      background: "#0a0d14",
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      color: "#e2e8f0",
      overflow: "hidden",
      // Prevent double-tap zoom on mobile
      touchAction: "manipulation",
    },
    topBar: {
      background: "linear-gradient(90deg, #0d1117 0%, #111827 100%)",
      borderBottom: "1px solid #1e2736",
      padding: isMobile ? "10px 14px" : "0 28px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxShadow: "0 1px 20px rgba(0,0,0,0.4)",
      height: isMobile ? "auto" : 56,
      minHeight: isMobile ? 52 : 56,
      flexShrink: 0,
      gap: 8,
    },
    logoArea: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexShrink: 0,
    },
    logoIcon: {
      width: isMobile ? 30 : 34,
      height: isMobile ? 30 : 34,
      background: "linear-gradient(135deg, #25d366, #128c7e)",
      borderRadius: 9,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: isMobile ? 15 : 18,
      boxShadow: "0 4px 14px rgba(37,211,102,0.35)",
      flexShrink: 0,
    },
    title: {
      fontSize: isMobile ? 15 : 17,
      fontWeight: 700,
      background: "linear-gradient(90deg, #e2e8f0, #94a3b8)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      letterSpacing: "-0.02em",
      whiteSpace: "nowrap",
    },
    topActions: {
      display: "flex",
      gap: isMobile ? 6 : 8,
      alignItems: "center",
    },
    topBtn: (active, accent) => ({
      display: "flex", alignItems: "center", gap: isMobile ? 0 : 6,
      padding: isMobile ? "7px 9px" : "7px 14px",
      borderRadius: 9,
      border: `1px solid ${active ? accent + "55" : "#1e2736"}`,
      background: active ? accent + "1a" : "#111827",
      color: active ? accent : "#64748b",
      fontSize: isMobile ? 11 : 12, fontWeight: 600, cursor: "pointer",
      transition: "all 0.2s",
      whiteSpace: "nowrap",
      minHeight: 36,
    }),
    // ── Layout ──
    layout: {
      display: "flex",
      flex: 1,
      overflow: "hidden",
    },
    // ── Sidebar ──
    sidebar: {
      width: isMobile ? "100%" : 320,
      minWidth: isMobile ? "100%" : 320,
      maxWidth: isMobile ? "100%" : 320,
      background: "#0d1117",
      borderRight: isMobile ? "none" : "1px solid #1e2736",
      display: showSidebar ? "flex" : "none",
      flexDirection: "column",
      overflow: "hidden",
      flex: isMobile ? 1 : "none",
    },
    sidebarHeader: {
      padding: "12px 14px",
      borderBottom: "1px solid #1e2736",
      background: "#0d1117",
      flexShrink: 0,
    },
    searchWrap: {
      position: "relative",
      marginBottom: 8,
    },
    searchIcon: {
      position: "absolute",
      left: 10, top: "50%",
      transform: "translateY(-50%)",
      color: "#475569",
      pointerEvents: "none",
    },
    searchInput: {
      width: "100%",
      background: "#111827",
      border: "1px solid #1e2736",
      borderRadius: 10,
      padding: "9px 10px 9px 34px",
      color: "#e2e8f0",
      fontSize: 13,
      outline: "none",
      boxSizing: "border-box",
      minHeight: 40,
    },
    filterSelect: {
      width: "100%",
      background: "#111827",
      border: "1px solid #1e2736",
      borderRadius: 10,
      padding: "8px 10px",
      color: "#94a3b8",
      fontSize: 13,
      outline: "none",
      boxSizing: "border-box",
      cursor: "pointer",
      minHeight: 38,
    },
    chatList: {
      flex: 1,
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
    },
    chatItem: (isSelected) => ({
      padding: "12px 14px",
      borderBottom: "1px solid #111827",
      cursor: "pointer",
      background: isSelected ? "linear-gradient(90deg, #0ea5e911, #6366f108)" : "transparent",
      borderLeft: isSelected ? "3px solid #0ea5e9" : "3px solid transparent",
      transition: "background 0.15s",
      WebkitTapHighlightColor: "transparent",
    }),
    chatItemRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
    },
    chatMeta: {
      flex: 1,
      minWidth: 0,
    },
    chatName: {
      fontSize: 14,
      fontWeight: 600,
      color: "#e2e8f0",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 4,
      marginBottom: 2,
    },
    chatPhone: {
      fontSize: 11,
      color: "#475569",
      marginBottom: 4,
    },
    chatPreview: {
      fontSize: 12,
      color: "#64748b",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      flex: 1,
    },
    unreadBadge: {
      background: "linear-gradient(135deg, #ef4444, #dc2626)",
      color: "#fff",
      fontSize: 10,
      fontWeight: 700,
      padding: "2px 6px",
      borderRadius: 20,
      minWidth: 18,
      textAlign: "center",
      flexShrink: 0,
      boxShadow: "0 2px 8px rgba(239,68,68,0.4)",
    },
    chatBadges: {
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
      marginTop: 4,
    },
    pausedBadge: {
      background: "#2a1f0a",
      color: "#fbbf24",
      padding: "2px 7px",
      borderRadius: 20,
      fontSize: 10,
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      gap: 3,
    },
    tagPill: {
      background: "#1e1b3a",
      color: "#a78bfa",
      padding: "2px 7px",
      borderRadius: 20,
      fontSize: 10,
      fontWeight: 600,
    },
    followupLine: (overdue) => ({
      fontSize: 11,
      marginTop: 4,
      display: "flex",
      alignItems: "center",
      gap: 4,
      color: overdue ? "#f87171" : "#a78bfa",
      fontWeight: overdue ? 700 : 400,
    }),
    // ── Main panel ──
    mainPanel: {
      flex: 1,
      display: showMainPanel ? "flex" : "none",
      flexDirection: "column",
      background: "#0a0d14",
      overflow: "hidden",
      minWidth: 0,
    },
    chatHeader: {
      padding: isMobile ? "10px 12px" : "0 20px",
      borderBottom: "1px solid #1e2736",
      background: "#0d1117",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      minHeight: isMobile ? 58 : 64,
      flexShrink: 0,
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
      flex: 1,
    },
    backBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      background: "transparent",
      border: "none",
      color: "#94a3b8",
      cursor: "pointer",
      flexShrink: 0,
      padding: 0,
      borderRadius: 8,
      WebkitTapHighlightColor: "transparent",
    },
    headerName: {
      fontSize: isMobile ? 14 : 15,
      fontWeight: 700,
      color: "#e2e8f0",
      letterSpacing: "-0.01em",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    headerSub: {
      fontSize: 11,
      color: "#475569",
      marginTop: 1,
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexShrink: 0,
    },
    // Desktop header actions
    desktopHeaderActions: {
      display: isMobile ? "none" : "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    moreBtn: {
      display: isMobile ? "flex" : "none",
      alignItems: "center",
      justifyContent: "center",
      width: 38,
      height: 38,
      background: "#111827",
      border: "1px solid #1e2736",
      borderRadius: 9,
      color: "#94a3b8",
      cursor: "pointer",
      flexShrink: 0,
      WebkitTapHighlightColor: "transparent",
    },
    statusBtnMobile: {
      display: isMobile ? "flex" : "none",
      alignItems: "center",
    },
    select: {
      background: "#111827",
      border: "1px solid #1e2736",
      borderRadius: 8,
      padding: "7px 10px",
      color: "#e2e8f0",
      fontSize: 12,
      outline: "none",
      cursor: "pointer",
      minHeight: 36,
    },
    datetimeInput: {
      background: "#111827",
      border: "1px solid #1e2736",
      borderRadius: 8,
      padding: "7px 8px",
      color: "#e2e8f0",
      fontSize: 12,
      outline: "none",
      minHeight: 36,
    },
    actionBtn: (color, active) => ({
      display: "flex", alignItems: "center", gap: 6,
      padding: "7px 14px",
      borderRadius: 8,
      border: "none",
      background: active !== undefined
        ? (active ? "#14532d" : "#450a0a")
        : color === "purple" ? "#4c1d95"
        : color === "blue" ? "#1e3a5f"
        : "#1e2736",
      color: active !== undefined
        ? (active ? "#4ade80" : "#f87171")
        : color === "purple" ? "#c4b5fd"
        : color === "blue" ? "#93c5fd"
        : "#e2e8f0",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "all 0.2s",
      minHeight: 36,
      WebkitTapHighlightColor: "transparent",
    }),
    // Desktop meta panel
    metaPanel: {
      display: isMobile ? "none" : "flex",
      background: "#0d1117",
      borderBottom: "1px solid #1e2736",
      padding: "10px 20px",
      gap: 8,
      alignItems: "center",
      flexWrap: "wrap",
      flexShrink: 0,
    },
    metaInput: {
      background: "#111827",
      border: "1px solid #1e2736",
      borderRadius: 8,
      padding: "8px 12px",
      color: "#e2e8f0",
      fontSize: 13,
      outline: "none",
      flex: 1,
      minWidth: 150,
      minHeight: 36,
      boxSizing: "border-box",
    },
    // ── Messages ──
    messagesArea: {
      flex: 1,
      overflowY: "auto",
      padding: isMobile ? "14px 12px" : "20px",
      display: "flex",
      flexDirection: "column",
      gap: 5,
      background: "radial-gradient(ellipse at top left, #0d1a2a 0%, #0a0d14 60%)",
      WebkitOverflowScrolling: "touch",
    },
    msgBubble: (outgoing) => ({
      maxWidth: isMobile ? "82%" : "68%",
      alignSelf: outgoing ? "flex-end" : "flex-start",
      background: outgoing
        ? "linear-gradient(135deg, #0ea5e9, #0284c7)"
        : "#151e2d",
      borderRadius: outgoing ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
      padding: "9px 13px",
      boxShadow: outgoing
        ? "0 4px 14px rgba(14,165,233,0.25)"
        : "0 2px 8px rgba(0,0,0,0.3)",
      border: outgoing ? "none" : "1px solid #1e2736",
    }),
    msgText: {
      fontSize: isMobile ? 13 : 13.5,
      color: "#e2e8f0",
      lineHeight: 1.55,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    msgMeta: {
      fontSize: 10,
      color: "rgba(226,232,240,0.5)",
      marginTop: 3,
      textAlign: "right",
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 4,
    },
    // ── Input area ──
    mediaBar: {
      display: showMediaBar ? "flex" : "none",
      borderTop: "1px solid #1e2736",
      padding: "10px 12px",
      background: "#0d1117",
      gap: 8,
      alignItems: "center",
      flexShrink: 0,
      flexWrap: isMobile ? "wrap" : "nowrap",
    },
    captionInput: {
      background: "#111827",
      border: "1px solid #1e2736",
      borderRadius: 8,
      padding: "8px 10px",
      color: "#e2e8f0",
      fontSize: 12,
      flex: 1,
      outline: "none",
      minWidth: 0,
      minHeight: 36,
    },
    sendMediaBtn: {
      background: "linear-gradient(135deg, #f97316, #ea580c)",
      border: "none",
      borderRadius: 8,
      padding: "8px 16px",
      color: "#fff",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      boxShadow: "0 4px 12px rgba(249,115,22,0.3)",
      whiteSpace: "nowrap",
      minHeight: 36,
    },
    replyBar: {
      borderTop: "1px solid #1e2736",
      padding: isMobile ? "8px 10px" : "10px 14px",
      background: "#0d1117",
      display: "flex",
      gap: 8,
      alignItems: "center",
      flexShrink: 0,
    },
    attachBtn: {
      width: 40, height: 40,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "#111827",
      border: "1px solid #1e2736",
      borderRadius: "50%",
      color: "#64748b",
      cursor: "pointer",
      flexShrink: 0,
      WebkitTapHighlightColor: "transparent",
    },
    replyInput: {
      flex: 1,
      background: "#111827",
      border: "1px solid #1e2736",
      borderRadius: 12,
      padding: isMobile ? "10px 14px" : "11px 16px",
      color: "#e2e8f0",
      fontSize: isMobile ? 14 : 14,
      outline: "none",
      minWidth: 0,
      minHeight: 40,
    },
    sendBtn: {
      width: 42, height: 42,
      background: "linear-gradient(135deg, #0ea5e9, #0284c7)",
      border: "none",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      cursor: "pointer",
      boxShadow: "0 4px 14px rgba(14,165,233,0.35)",
      flexShrink: 0,
      WebkitTapHighlightColor: "transparent",
    },
    emptyState: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      color: "#1e2736",
      gap: 14,
    },
    emptyIcon: {
      width: 72, height: 72,
      background: "linear-gradient(135deg, #25d36611, #128c7e11)",
      borderRadius: 22,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 32,
      border: "1px solid #1e2736",
    },
    // Mobile drawer content
    drawerLabel: {
      fontSize: 11,
      color: "#475569",
      marginBottom: 6,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      fontWeight: 600,
    },
    drawerSection: {
      marginBottom: 16,
    },
    drawerInput: {
      width: "100%",
      background: "#111827",
      border: "1px solid #1e2736",
      borderRadius: 10,
      padding: "11px 14px",
      color: "#e2e8f0",
      fontSize: 14,
      outline: "none",
      boxSizing: "border-box",
      minHeight: 44,
    },
    drawerRow: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
    },
    drawerBtn: (color, active) => ({
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: "11px 14px",
      borderRadius: 10,
      border: "none",
      minHeight: 44,
      background: active !== undefined
        ? (active ? "#14532d" : "#450a0a")
        : color === "purple" ? "#4c1d95"
        : color === "blue" ? "#1e3a5f"
        : "#1e2736",
      color: active !== undefined
        ? (active ? "#4ade80" : "#f87171")
        : color === "purple" ? "#c4b5fd"
        : color === "blue" ? "#93c5fd"
        : "#e2e8f0",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      WebkitTapHighlightColor: "transparent",
    }),
  };

  // ── Mobile Meta Drawer content ────────────────────────────────────────────
  const MetaContent = ({ inDrawer = false }) => (
    <>
      <div style={inDrawer ? S.drawerSection : { marginBottom: 0, display: "flex", gap: 8, flex: 1, flexWrap: "wrap", alignItems: "center" }}>
        {inDrawer && <div style={S.drawerLabel}>Lead Status</div>}
        <select
          value={leadStatus}
          onChange={(e) => updateLeadStatus(e.target.value)}
          style={inDrawer ? { ...S.drawerInput, cursor: "pointer" } : S.select}
        >
          <option value="New">New</option>
          <option value="Interested">Interested</option>
          <option value="Not Interested">Not Interested</option>
          <option value="Booked">Booked</option>
          <option value="Follow-up">Follow-up</option>
          <option value="Closed">Closed</option>
        </select>

        {inDrawer && (
          <>
            <div style={{ ...S.drawerSection, marginTop: 16 }}>
              <div style={S.drawerLabel}>Schedule Follow-up</div>
              <div style={{ ...S.drawerRow, gap: 8 }}>
                <input
                  type="datetime-local"
                  value={followupAt}
                  onChange={(e) => setFollowupAt(e.target.value)}
                  style={{ ...S.drawerInput, flex: 1 }}
                />
                <button onClick={() => { saveFollowup(); setShowMetaDrawer(false); }} style={S.drawerBtn("purple")}>
                  <ClockIcon /> Save
                </button>
              </div>
            </div>

            <div style={{ ...S.drawerSection }}>
              <div style={S.drawerLabel}>Bot Control</div>
              <button onClick={() => { toggleBotForChat(); setShowMetaDrawer(false); }} style={S.drawerBtn(null, botPaused)}>
                <BotIcon /> {botPaused ? "Resume Bot" : "Pause Bot"}
              </button>
            </div>

            <div style={{ ...S.drawerSection }}>
              <div style={S.drawerLabel}>Tags</div>
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                style={S.drawerInput}
                placeholder="HOT, VIP, Follow-up"
              />
            </div>

            <div style={{ ...S.drawerSection }}>
              <div style={S.drawerLabel}>Internal Notes</div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{ ...S.drawerInput, height: 80, resize: "none" }}
                placeholder="Add internal notes..."
              />
            </div>

            <button onClick={() => { saveNotesTags(); setShowMetaDrawer(false); }} style={S.drawerBtn("blue")}>
              Save Notes & Tags
            </button>
          </>
        )}
      </div>
    </>
  );

  return (
    <div style={S.root}>
      {/* ── Top Bar ── */}
      <div style={S.topBar}>
        <div style={S.logoArea}>
          <div style={S.logoIcon}>💬</div>
          <span style={S.title}>WhatsApp Inbox</span>
        </div>
        <div style={S.topActions}>
          <button
            style={S.topBtn(soundEnabled, "#25d366")}
            onClick={() => { setSoundEnabled(true); playBeep(); }}
          >
            <VolumeIcon />
            {!isMobile && (soundEnabled ? " Sound ON" : " Enable Sound")}
          </button>
          <button
            style={S.topBtn(false, "#60a5fa")}
            onClick={async () => {
              if (!("Notification" in window)) { alert("This browser does not support notifications"); return; }
              const permission = await Notification.requestPermission();
              if (permission === "granted") {
                new Notification("CRM Notifications Enabled ✅", {
                  body: "Ab new WhatsApp message par notification aayegi.",
                  icon: "/logo192.png"
                });
              } else { alert("Notification permission blocked ❌"); }
            }}
          >
            <BellIcon />
            {!isMobile && " Enable Notifications"}
          </button>
        </div>
      </div>

      {/* ── Main Layout ── */}
      <div style={S.layout}>
        {/* ── Sidebar ── */}
        <div style={S.sidebar}>
          <div style={S.sidebarHeader}>
            <div style={S.searchWrap}>
              <span style={S.searchIcon}><SearchIcon /></span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={S.searchInput}
                placeholder="Search chats..."
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={S.filterSelect}
            >
              <option value="all">All Chats</option>
              <option value="unread">Unread Only</option>
              <option value="read">Read Only</option>
              <option value="followup">Follow-up Only</option>
              <option value="interested">Interested</option>
              <option value="booked">Booked</option>
              <option value="new">New Leads</option>
              <option value="due_today">Due Today</option>
              <option value="overdue">Overdue</option>
            </select>
            <button
              onClick={downloadContactsCSV}
              style={{
                width: "100%",
                marginTop: 8,
                background: "linear-gradient(135deg, #16a34a, #15803d)",
                border: "none",
                borderRadius: 10,
                padding: "9px 12px",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Download Contacts
            </button>
          </div>

          <div style={S.chatList}>
            {filteredChats.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "#334155", fontSize: 13 }}>
                No chats found
              </div>
            )}
            {filteredChats.map((chat) => {
              const isSelected = selected === chat.phone;
              const unread = Number(chat.unread_count || 0);
              const overdue = chat.followup_at && new Date(chat.followup_at) < new Date();
              return (
                <div
                  key={chat.phone}
                  onClick={() => loadMessages(chat.phone)}
                  style={S.chatItem(isSelected)}
                >
                  <div style={S.chatItemRow}>
                    <Avatar name={chat.profile_name || chat.phone} size={isMobile ? 40 : 38} />
                    <div style={S.chatMeta}>
                      <div style={S.chatName}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: isMobile ? 160 : 130 }}>
                          {chat.profile_name || chat.phone}
                        </span>
                        <span style={{ fontSize: 10, color: "#64748b", whiteSpace: "nowrap", flexShrink: 0 }}>
                          {formatShortTime(chat.last_time || chat.last_message_time || chat.created_at)}
                        </span>
                      </div>
                      <div style={S.chatPhone}>{chat.phone}</div>
                      {(chat.city || chat.service_interest) && (
                        <div style={{ fontSize: 11.5, color: "#8a93a8", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 190 }}>
                          {[chat.city, chat.service_interest].filter(Boolean).join(" • ")}
                        </div>
                      )}
                      <div style={S.chatBadges}>
                        <StatusBadge status={chat.lead_status} />
                        {Number(chat.human_handover || 0) === 1 && (
                          <span style={S.pausedBadge}><BotIcon /> Paused</span>
                        )}
                        {chat.tags && String(chat.tags).split(",").map((tag, i) => (
                          <span key={i} style={S.tagPill}>{tag.trim()}</span>
                        ))}
                      </div>
                      {chat.followup_at && (
                        <div style={S.followupLine(overdue)}>
                          <ClockIcon />
                          {formatDate(chat.followup_at)}
                        </div>
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginTop: 3 }}>
                        <div style={S.chatPreview}>{chat.last_message || ""}</div>
                        {unread > 0 && <span style={S.unreadBadge}>{chat.unread_count}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Main Panel ── */}
        <div style={S.mainPanel}>
          {/* Chat Header */}
          <div style={S.chatHeader}>
            {selected ? (
              <>
                <div style={S.headerLeft}>
                  {isMobile && (
                    <button
                      style={S.backBtn}
                      onClick={() => setSelected(null)}
                    >
                      <ChevronLeft />
                    </button>
                  )}
                  <Avatar name={selectedChat?.profile_name || selected} size={isMobile ? 36 : 38} />
                  <div style={{ minWidth: 0 }}>
                    <div style={S.headerName}>{selectedChat?.profile_name || selected}</div>
                    <div style={S.headerSub}>{selected}</div>
                  </div>
                </div>

                {/* Desktop header actions */}
                <div style={S.desktopHeaderActions}>
                  <select value={leadStatus} onChange={(e) => updateLeadStatus(e.target.value)} style={S.select}>
                    <option value="New">New</option>
                    <option value="Interested">Interested</option>
                    <option value="Not Interested">Not Interested</option>
                    <option value="Booked">Booked</option>
                    <option value="Follow-up">Follow-up</option>
                    <option value="Closed">Closed</option>
                  </select>
                  <input type="datetime-local" value={followupAt} onChange={(e) => setFollowupAt(e.target.value)} style={S.datetimeInput} />
                  <button onClick={saveFollowup} style={S.actionBtn("purple")}>
                    <ClockIcon /> Save Follow-up
                  </button>
                  <button onClick={toggleBotForChat} style={S.actionBtn(null, botPaused)}>
                    <BotIcon /> {botPaused ? "Resume Bot" : "Pause Bot"}
                  </button>
                </div>

                {/* Mobile: show status badge + more button */}
                {isMobile && (
                  <div style={S.headerActions}>
                    <StatusBadge status={leadStatus} />
                    <button
                      style={S.moreBtn}
                      onClick={() => setShowMetaDrawer(true)}
                    >
                      <MoreIcon />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <span style={{ color: "#334155", fontSize: 15 }}>Select a conversation</span>
            )}
          </div>

          {/* Desktop Notes & Tags Strip */}
          {selected && (
            <div style={S.metaPanel}>
              <span style={{ color: "#475569", fontSize: 12, flexShrink: 0 }}><TagIcon /></span>
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                style={{ ...S.metaInput, flex: "0 1 200px" }}
                placeholder="Tags: HOT, VIP, Follow-up"
              />
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{ ...S.metaInput, resize: "none", height: 36, lineHeight: "20px" }}
                rows="1"
                placeholder="Internal notes..."
              />
              <button onClick={saveNotesTags} style={{ ...S.actionBtn("blue"), padding: "8px 16px" }}>
                Save Notes & Tags
              </button>
            </div>
          )}

          {/* Messages */}
          <div
            ref={messagesBoxRef}
            onScroll={handleMessagesScroll}
            style={S.messagesArea}
          >
            {!selected && (
              <div style={S.emptyState}>
                <div style={S.emptyIcon}>💬</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#334155" }}>No conversation selected</div>
                <div style={{ fontSize: 13, color: "#1e2736" }}>Pick a chat from the sidebar to get started</div>
              </div>
            )}

            {messages.map((m) => {
              const outgoing = m.direction === "outgoing";
              return (
                <div key={m.id} style={{ display: "flex", flexDirection: "column" }}>
                  <div style={S.msgBubble(outgoing)}>
                    {m.media_url ? (
  <>
                      {m.message_type === "image" && (
                        <img
                          src={`${process.env.REACT_APP_API_BASE || "https://api.elegentclinic.com"}${m.media_url}`}
                          alt="WhatsApp"
                          style={{
                            maxWidth: isMobile ? 200 : 220,
                            borderRadius: 10,
                            marginBottom: 6,
                            display: "block"
                          }}
                        />
                      )}

                      {m.message_type === "video" && (
                        <video
                          controls
                          style={{
                            maxWidth: isMobile ? 220 : 260,
                            borderRadius: 10,
                            marginBottom: 6
                          }}
                        >
                          <source
                            src={`${process.env.REACT_APP_API_BASE || "https://api.elegentclinic.com"}${m.media_url}`}
                          />
                        </video>
                      )}

                      {String(m.message_type || "").toLowerCase() === "audio" && m.media_url && (
                        <audio
                          controls
                          preload="metadata"
                          style={{
                            width: isMobile ? 220 : 260,
                            marginBottom: 6
                          }}
                        >
                          <source
                            src={`${process.env.REACT_APP_API_BASE || "https://api.elegentclinic.com"}${m.media_url}`}
                          />
                          Your browser does not support audio.
                        </audio>
                      )}

                      {m.message_type === "document" && (
                        <a
                          href={`${process.env.REACT_APP_API_BASE || "https://api.elegentclinic.com"}${m.media_url}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: "#60a5fa",
                            textDecoration: "none",
                            fontWeight: 600
                          }}
                        >
                          📄 Open Document
                        </a>
                      )}

                      {m.message_text && (
                        <div style={S.msgText}>{m.message_text}</div>
                      )}
                    </>
                  ) : (
                    <div style={S.msgText}>{m.message_text}</div>
                  )}
                    <div style={S.msgMeta}>
                      {Number(m.is_bot) === 1 && (
                        <span style={{ color: "#60a5fa", fontSize: 9 }}>
                          ⚡ bot
                        </span>
                      )}

                     

                      <span style={{ fontSize: 10 }}>
                        {formatShortTime(
                          m.created_at ||
                          m.createdAt ||
                          m.timestamp ||
                          m.sent_at
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Media Upload Bar (toggled by paperclip) */}
          {selected && (
            <div style={S.mediaBar}>
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                style={{
                  background: "#111827",
                  border: "1px solid #1e2736",
                  borderRadius: 8,
                  padding: "7px 10px",
                  color: "#64748b",
                  fontSize: 12,
                  flex: 1,
                  outline: "none",
                  minWidth: 0,
                }}
              />
              <input
                value={mediaCaption}
                onChange={(e) => setMediaCaption(e.target.value)}
                style={S.captionInput}
                placeholder="Caption (optional)"
              />
              <button onClick={sendMedia} disabled={sendingMedia} style={{ ...S.sendMediaBtn, opacity: sendingMedia ? 0.6 : 1, cursor: sendingMedia ? "not-allowed" : "pointer" }}>{sendingMedia ? "Sending..." : "Send Media"}</button>
            </div>
          )}

          {/* Reply Bar */}
          {selected && (
            <div style={S.replyBar}>
              {/* Paperclip toggle */}
              <button
                style={S.attachBtn}
                onClick={() => setShowMediaBar(v => !v)}
                title="Attach media"
              >
                <PaperclipIcon />
              </button>
              <input
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !sending) sendReply(); }}
                style={S.replyInput}
                placeholder="Type a message..."
                disabled={sending}
              />
              <button onClick={sendReply} disabled={sending} style={{ ...S.sendBtn, opacity: sending ? 0.6 : 1, cursor: sending ? "not-allowed" : "pointer" }}>
                <SendIcon />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile Meta Drawer ── */}
      {isMobile && (
        <MobileMetaDrawer open={showMetaDrawer} onClose={() => setShowMetaDrawer(false)}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0", marginBottom: 16 }}>
            Chat Settings
          </div>
          <MetaContent inDrawer={true} />
        </MobileMetaDrawer>
      )}
    </div>
  );
}